import { Button } from "@msuite/ui/button";
import { Icon } from "@msuite/ui/icons";
import { useQueryState } from "@msuite/ui/libs/nuqs";
import { Panels } from "@msuite/ui/panels";
import { Timeline } from "@msuite/ui/timeline";
import { uniqueArray } from "@msuite/utils/array";
import { generateId } from "@msuite/utils/strings";
import { useMemo } from "react";
import { VehicleOrderCard } from "@/components/cards/vehicle-order-card";
import { useVehicleIntegration } from "@/hooks/use-vehicle-integration";
import type { VehiclePanelBaseProps } from "../../shared";
import { VehiclePosition } from "../vehicle-general/vehicle-position";
import { VehicleWebfleetInformation } from "../vehicle-general/vehicle-webfleet-information";

export function VehicleOrders({ vehicleId }: VehiclePanelBaseProps) {
	/** State */
	const [year, setYear] = useQueryState("order-year", {
		defaultValue: new Date().getFullYear().toString(),
	});

	/** Hooks */
	const { data, isLoading } = useVehicleIntegration(vehicleId, {
		startDate: `${year}-01-01`,
		endDate: `${year}-12-31`,
	});

	const timeline = useMemo(() => {
		const orders = data?.integrationData?.orders || [];
		const dates = uniqueArray(orders.map((o) => o.orderdate)).sort((a, b) => b.localeCompare(a));
		return dates.map((date) => ({
			date,
			items: orders
				.filter((o) => o.orderdate === date)
				.map((order) => ({
					type: "order" as const,
					item: { ...order, id: generateId() },
				}))
				.sort((a, b) => b.item.orderstate_time?.localeCompare(a.item.orderstate_time)),
		}));
	}, [data]);

	function handleOnGoBack() {
		const newYear = Number.parseInt(year, 10) - 1;
		setYear(newYear.toString());
	}

	function handleOnGoForward() {
		const newYear = Number.parseInt(year, 10) + 1;
		setYear(newYear.toString());
	}

	/** Render */
	return (
		<Panels>
			<Panels.Panel
				flex={1}
				className="bottom-space"
			>
				<VehiclePosition
					vehicleId={vehicleId}
					key="vehicle-position"
				/>
				<VehicleWebfleetInformation
					vehicleId={vehicleId}
					key="vehicle-webfleet-information"
				/>
			</Panels.Panel>
			<Panels.Panel flex={2}>
				<Timeline
					controls={
						<div className="h-stack gap-2">
							<Button
								icon={<Icon name="ChevronLeft" />}
								size="xxs"
								onClick={handleOnGoBack}
							/>
							<div className="card py-0 self-stretch flex flex-row items-center rounded-lg">
								<p className="text-sm font-medium">{year}</p>
							</div>
							<Button
								icon={<Icon name="ChevronRight" />}
								size="xxs"
								onClick={handleOnGoForward}
							/>
						</div>
					}
					timeline={timeline}
					isLoading={isLoading}
					renderItem={(type, order) => {
						if (type === "order") {
							return <VehicleOrderCard order={order} />;
						}
						return null;
					}}
					renderItemMarker={() => (
						<Icon
							name="Circle"
							color="var(--color-accent-500)"
						/>
					)}
					refetch={async () => {}}
				/>
			</Panels.Panel>
		</Panels>
	);
}
